import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { ArrowLeft, Loader2, Shield, ShieldCheck, ExternalLink, Search } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "@/i18n/LanguageContext";
import LangSwitcher from "@/components/LangSwitcher";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminMembersPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [members, setMembers] = useState([]);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const meRes = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setMe(meRes.data);
        if (!meRes.data?.is_admin) {
          toast.error(t('vc.admin.access-required'));
          navigate("/");
          return;
        }
        const { data } = await axios.get(`${API}/admin/members`, { withCredentials: true });
        setMembers(data.members || []);
      } catch {
        navigate("/login");
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate, t]);

  const toggleAdmin = async (userId) => {
    try {
      const { data } = await axios.post(
        `${API}/admin/members/${userId}/toggle-admin`,
        {},
        { withCredentials: true },
      );
      setMembers((prev) => prev.map((m) =>
        m.user_id === userId ? { ...m, is_admin: data.is_admin } : m
      ));
      toast.success(data.is_admin ? t('vc.admin.granted') : t('vc.admin.revoked'));
    } catch {
      toast.error(t('vc.admin.toggle-fail'));
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#050505]">
      <Loader2 className="animate-spin text-[#F5F5F5]" size={28} />
    </div>;
  }

  const filtered = query
    ? members.filter((m) => (
        (m.name || "").toLowerCase().includes(query.toLowerCase())
        || (m.email || "").toLowerCase().includes(query.toLowerCase())
        || (m.member_id || "").toLowerCase().includes(query.toLowerCase())
      ))
    : members;

  return (
    <div className="min-h-screen bg-[#050505] text-white marble-bg">
      <Helmet>
        <title>{`${t('vc.admin.console')} · ${t('vc.members')} · VangardClub`}</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <header className="border-b border-white/[0.06] backdrop-blur-xl bg-[#050505]/80">
        <div className="max-w-6xl mx-auto px-5 h-[54px] flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-neutral-500 hover:text-white transition-colors"
            data-testid="admin-back-btn"
          >
            <ArrowLeft size={14} /> <span className="text-[10px] tracking-[0.2em] uppercase">VangardClub</span>
          </button>
          <div className="flex items-center gap-3">
            <LangSwitcher />
            <span className="text-[9px] uppercase tracking-[0.25em] text-neutral-400">{t('vc.admin.console')}</span>
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-5 pt-8 pb-16" data-testid="admin-members-page">
        <div className="flex items-baseline justify-between mb-4">
          <h1 className="font-serif text-2xl">{t('vc.admin.title')}</h1>
          <span className="text-[10px] tracking-[0.2em] uppercase text-neutral-500">{t('vc.admin.total', { n: members.length })}</span>
        </div>

        <div className="relative mb-4 max-w-sm">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('vc.admin.search-ph')}
            data-testid="admin-search"
            className="w-full pl-9 pr-3 py-2 bg-transparent border border-white/15 rounded-full focus:border-white/60 outline-none text-[13px]"
          />
        </div>

        <div className="border border-white/[0.08] rounded-2xl overflow-hidden bg-[#0A0A0A]/80">
          <div className="overflow-x-auto no-scrollbar">
          <table className="w-full text-sm min-w-[560px]">
            <thead className="bg-white/[0.02] text-[9px] uppercase tracking-[0.2em] text-neutral-500">
              <tr>
                <th className="text-left px-4 py-2.5 font-normal">{t('vc.admin.h-member')}</th>
                <th className="text-left px-4 py-2.5 font-normal hidden md:table-cell">{t('vc.admin.h-email')}</th>
                <th className="text-left px-4 py-2.5 font-normal">№</th>
                <th className="text-left px-4 py-2.5 font-normal hidden md:table-cell">{t('vc.admin.h-joined')}</th>
                <th className="text-right px-4 py-2.5 font-normal">{t('vc.admin.h-admin')}</th>
                <th className="text-right px-4 py-2.5 font-normal">{t('vc.admin.h-profile')}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((m) => (
                <tr key={m.user_id} className="border-t border-white/[0.05] hover:bg-white/[0.02]" data-testid={`admin-row-${m.user_id}`}>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2.5">
                      {m.picture ? (
                        <img src={m.picture} alt={`${m.name || t('vc.member')} portrait`} className="w-7 h-7 rounded-full object-cover border border-white/10" />
                      ) : (
                        <span className="w-7 h-7 rounded-full flex items-center justify-center bg-[#0F0F0F] border border-white/20 text-[10px]">
                          {((m.name || "?").split(" ").map((w) => w[0]).join("")).slice(0, 2).toUpperCase()}
                        </span>
                      )}
                      <span className="text-[13px] truncate max-w-[12rem]">{m.name || "—"}</span>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 hidden md:table-cell text-[12px] text-neutral-400">{m.email}</td>
                  <td className="px-4 py-2.5 font-mono tabular-nums text-[12px]">{m.member_id || "—"}</td>
                  <td className="px-4 py-2.5 hidden md:table-cell text-neutral-500 text-[11px]">
                    {m.member_since ? new Date(m.member_since).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <button
                      onClick={() => toggleAdmin(m.user_id)}
                      disabled={m.user_id === me?.user_id}
                      title={m.user_id === me?.user_id ? t('vc.admin.cant-remove-self') : ""}
                      data-testid={`admin-toggle-${m.user_id}`}
                      className={`inline-flex items-center gap-1.5 text-[9px] tracking-[0.15em] uppercase px-2.5 py-1 rounded-full border ${
                        m.is_admin
                          ? "border-white/60 text-white bg-white/10"
                          : "border-white/10 text-neutral-400 hover:border-white/30"
                      } disabled:opacity-40`}
                    >
                      {m.is_admin ? <ShieldCheck size={11} /> : <Shield size={11} />}
                      {m.is_admin ? t('vc.admin.is-admin') : t('vc.admin.is-member')}
                    </button>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    {m.slug && (
                      <Link
                        to={`/u/${m.slug}`}
                        target="_blank"
                        className="inline-flex items-center gap-1 text-[11px] text-neutral-300 hover:text-white hover:underline"
                      >
                        {t('vc.admin.view')} <ExternalLink size={10} />
                      </Link>
                    )}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-neutral-500 text-sm">{t('vc.admin.no-results')}</td>
                </tr>
              )}
            </tbody>
          </table>
          </div>
        </div>
      </main>
    </div>
  );
}
